import { Sparkles, Paintbrush, Hammer, ArrowRight } from "lucide-react";
import { useFadeIn } from "@/hooks/useFadeIn";
import { Link } from "react-router-dom";
import cleaningHero from "@/assets/cleaning-hero.jpg";
import paintingHero from "@/assets/painting-hero.jpg";
import carpentryHero from "@/assets/carpentry-hero.jpg";

const services = [
  {
    icon: Sparkles,
    title: "Professional Cleaning",
    description:
      "Deep cleaning and detail work that improves first impressions and presentation.",
    image: cleaningHero,
  },
  {
    icon: Paintbrush,
    title: "Interior & Exterior Painting",
    description:
      "Targeted color and finish updates that make your property feel fresh and market-ready.",
    image: paintingHero,
  },
  {
    icon: Hammer,
    title: "Carpentry & Repairs",
    description:
      "High-impact fixes that reduce buyer doubt and support stronger offers.",
    image: carpentryHero,
  },
];

const PropertyPrepSection = () => {
  const { ref, visible } = useFadeIn();

  return (
    <section className="pt-20 pb-24 md:pt-24 md:pb-28 bg-[#f7f4ee]">
      <div
        ref={ref}
        className={`mx-auto max-w-wide px-5 md:px-12 transition-all duration-700 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-20">
          <div className="max-w-xl">
            <span className="font-body text-[10px] uppercase tracking-[0.3em] text-accent mb-4 block">
              Our Advantage
            </span>
            <h2 className="font-headline text-4xl md:text-[3.1rem] lg:text-[3.45rem] text-foreground mb-6 leading-[1.08]">
              We Prepare Your Property to Perform
            </h2>
            <div className="border-l border-accent/65 pl-5 md:pl-6">
              <p className="font-body text-xs uppercase tracking-[0.18em] text-accent/90 mb-4">
                Better presentation. Stronger offers. Faster sales.
              </p>
              <p className="text-muted-foreground text-base md:text-lg leading-[1.8] max-w-lg">
                We do not just list homes. We prepare them with the right upgrades, repairs, and finishing work so they show better and perform better in the market.
              </p>
            </div>
            <Link
              to="/property-preparation"
              className="mt-8 inline-flex items-center gap-2 bg-accent px-6 py-3 font-body text-sm uppercase tracking-[0.14em] text-white transition-all duration-300 hover:bg-accent/90 hover:translate-x-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#f7f4ee]"
            >
              Explore Property Preparation
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="space-y-4 md:space-y-5">
            {services.map((service, index) => (
              <div
                key={service.title}
                className={`group grid grid-cols-[92px_1fr] md:grid-cols-[118px_1fr] gap-5 md:gap-6 border border-border/80 bg-[#fbf8f2] p-4 md:p-5 transition-colors duration-300 hover:bg-[#f3ede2] ${
                  index > 0 ? "border-t border-neutral-200" : ""
                }`}
              >
                <div>
                  <div className="relative h-full min-h-[104px] overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-transparent" />
                    <div className="relative z-10 flex h-full items-start justify-start p-3">
                      <service.icon
                        className="w-5 h-5 text-accent transition-transform duration-300 group-hover:translate-x-0.5"
                        strokeWidth={1.55}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col justify-center">
                  <div className="mb-2 flex items-center gap-3">
                    <span className="font-body text-[10px] uppercase tracking-[0.24em] text-accent">
                      0{index + 1}
                    </span>
                    <div className="h-px flex-1 bg-border/80" />
                  </div>
                  <h3 className="font-headline text-2xl md:text-[2rem] text-foreground mb-2.5 leading-tight">
                    {service.title}
                  </h3>
                  <p className="max-w-xl text-muted-foreground text-base leading-[1.75]">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PropertyPrepSection;
